import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CreateVendorSettingsRequest,
  KpiCategoryResponse,
  KpiMasterResponse,
  SaveKpiScoreBandsRequest,
  ScoringEngineRunRequest,
  UpdateVendorSettingsRequest,
  VendorSpecificSettingsApiResponse,
} from '../models/vsc-settings.model';
import {
  FinalScoreRequest,
  FinalScoreResponse,
  KpiCategoryScoreRequest,
  KpiCategoryScoreResponse,
  KpiScoreRequest,
  KpiScoreResponse,
  KpiScoreWeeklyRequest,
  KpiScoreWeeklyResponse,
} from '../models/vsc-scorecard.model';
import { VendorScoreGscViewRequest, VendorScoreGscViewResponse } from '../models/vsc-gsc-view.model';
import { utils } from '../helper/utils';

@Injectable({
  providedIn: 'root',
})
export class VscKpiCategoryService {
  private readonly http = inject(HttpClient);

  getKpiCategories(): Observable<KpiCategoryResponse[]> {
    return this.http.get<KpiCategoryResponse[]>(utils.GetVendorScoreDataUrl('kpi-categories'));
  }

  getKpiMaster(): Observable<KpiMasterResponse[]> {
    return this.http.get<KpiMasterResponse[]>(utils.GetVendorScoreDataUrl('kpi-master'));
  }

  getVendorSettingsByCode(vendorCode: string): Observable<VendorSpecificSettingsApiResponse> {
    return this.http.get<VendorSpecificSettingsApiResponse>(utils.GetVendorScoreSettingsUrl(`vendor-settings/${vendorCode}`));
  }

  saveKpiScoreBands(categoryId: string, request: SaveKpiScoreBandsRequest): Observable<void> {
    return this.http.put<void>(utils.GetVendorScoreDataUrl(`kpi-categories/${categoryId}/score-bands`), request);
  }

  getVendorSettingsProfiles(): Observable<VendorSpecificSettingsApiResponse[]> {
    return this.http.get<VendorSpecificSettingsApiResponse[]>(utils.GetVendorScoreSettingsUrl('vendor-settings'));
  }

  updateVendorSettings(request: UpdateVendorSettingsRequest): Observable<void> {
    return this.http.put<void>(utils.GetVendorScoreSettingsUrl('vendor-settings'), request);
  }

  createVendorSettings(request: CreateVendorSettingsRequest): Observable<VendorSpecificSettingsApiResponse> {
    return this.http.post<VendorSpecificSettingsApiResponse>(utils.GetVendorScoreSettingsUrl('vendor-settings'), request);
  }

  postFinalScore(request: FinalScoreRequest): Observable<FinalScoreResponse> {
    return this.http.post<FinalScoreResponse>(utils.GetVendorScoreDataUrl('final-score'), request);
  }

  postKpiCategoryScore(request: KpiCategoryScoreRequest): Observable<KpiCategoryScoreResponse> {
    return this.http.post<KpiCategoryScoreResponse>(utils.GetVendorScoreDataUrl('kpi-category-score'), request);
  }

  postKpiScore(request: KpiScoreRequest): Observable<KpiScoreResponse> {
    return this.http.post<KpiScoreResponse>(utils.GetVendorScoreDataUrl('kpi-score'), request);
  }

  postKpiScoreWeekly(request: KpiScoreWeeklyRequest): Observable<KpiScoreWeeklyResponse> {
    return this.http.post<KpiScoreWeeklyResponse>(utils.GetVendorScoreDataUrl('kpi-score-weekly'), request);
  }

  postGscView(request: VendorScoreGscViewRequest): Observable<VendorScoreGscViewResponse> {
    return this.http.post<VendorScoreGscViewResponse>(utils.GetVendorScoreDataUrl('vendor-score-gsc-view'), request);
  }

  runScoringEngine(request: ScoringEngineRunRequest): Observable<unknown> {
    return this.http.post<unknown>(utils.GetVendorScoreDataUrl('scoring-engine/run'), request);
  }

  deleteVendorSettings(id: number, deletedById: number, deletedAt: string): Observable<void> {
    return this.http.delete<void>(utils.GetVendorScoreSettingsUrl(`vendor-settings/${id}`), {
      params: { deletedById: String(deletedById), deletedAt },
    });
  }
}
